import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import { CAPACITY_DEVELOPMENT_QUESTION_ID } from 'shared/providers/ProgramProvider/utils';
import { getActivityQuestionText } from 'npo/pages/ReportBuilder/Components/Activities/ActivityQuestionText';
import {
  getQuestionOptions,
  serviceDeliveryQuestionId,
  serviceSubTypeQuestionId,
} from 'npo/pages/ReportBuilder/Components/Activities/options';
import QuestionRenderer from './QuestionRenderer';
import ActivityQuestionRenderer, {
  isActivityQuestion,
  HELPER_TEXT_AS_TITLE_QUESTION_IDS,
} from './ActivityQuestionRenderer';
import { ReportsTable } from '../ReportGroup';
import StatusIcon from './StatusIcon';
import { isPrefilledFromProgram } from './prefill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles, faArrowRight } from '@fortawesome/pro-light-svg-icons';

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${SPACERS.large};
  padding-top: calc(${SPACERS.large} + ${SPACERS.medium});
  gap: ${SPACERS.medium};
  overflow: auto;
  height: 100%;
  background-color: ${COLORS.whiteBase};
  ${thinScrollbarStyles}

  .question-title-text {
    font-size: 1.3rem;
    margin-bottom: 0;
  }

  .question-card {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.medium};
    .primary-note {
      text-align: center;
    }
  }

  .question-header {
    display: flex;
    justify-content: center;
    gap: ${SPACERS.small};
    svg {
      margin-top: ${SPACERS.extraTiny};
    }
  }

  .required-badge {
    color: ${COLORS.critical};
    margin-left: ${SPACERS.extraTiny};
  }

  .help-text {
    text-align: center;
    color: ${COLORS.greyDark};
    margin-top: -${SPACERS.tiny};
  }

  .prefilled-callout {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${SPACERS.medium};
    padding: ${SPACERS.small} ${SPACERS.medium};
    border-radius: 10px;
    border: 1px solid ${COLORS.successSubdued}99;
    box-shadow: 0 1px 2px rgba(33, 150, 83, 0.06),
      0 6px 18px -8px rgba(33, 150, 83, 0.25);
    overflow: hidden;
    animation: ${fadeSlideIn} 0.35s ease-out both;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${COLORS.success};
    }

    .prefilled-icon {
      flex-shrink: 0;
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: ${COLORS.whiteBase};
      border: 1px solid ${COLORS.successSubdued};
      color: ${COLORS.success};
      svg {
        width: 16px;
        height: 16px;
      }
    }

    .prefilled-copy {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
      .prefilled-title {
        margin: 0;
        color: ${COLORS.greyDarkest};
      }
      .prefilled-body {
        margin: 0;
        color: ${COLORS.greyDark};
      }
    }

    .prefilled-cta {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: ${SPACERS.extraTiny};
      padding: 6px 12px;
      border-radius: 999px;
      background: ${COLORS.whiteBase};
      border: 1px solid ${COLORS.successSubdued};
      color: ${COLORS.success};
      font: inherit;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      cursor: pointer;
      transition: background-color 0.2s ease, border-color 0.2s ease,
        transform 0.2s ease;
      svg {
        width: 10px;
        height: 10px;
        transition: transform 0.2s ease;
      }
      &:hover:not(:disabled),
      &:focus-visible {
        background: ${COLORS.success};
        border-color: ${COLORS.success};
        color: ${COLORS.whiteBase};
        outline: none;
        svg {
          transform: translateX(2px);
        }
      }
      &:focus-visible {
        box-shadow: 0 0 0 3px ${COLORS.successBackground};
      }
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }

    @media (max-width: 640px) {
      flex-wrap: wrap;
      .prefilled-cta {
        margin-left: calc(36px + ${SPACERS.medium});
      }
    }
  }

  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: ${COLORS.greyDark};
  }

  textarea#description-box {
    border-radius: 4px;
    border: 1px solid ${COLORS.grayBase};
  }
`;

export const emptyInput = (): AnswerInput => ({
  text: null,
  date1: null,
  date2: null,
  answerId: null,
});

export const inputFromAnswer = (
  answer:
    | {
        text: string | null;
        date1: string | null;
        date2: string | null;
        answerId: number | null;
        questionText: string | null;
        enumResponse?: { dbId: number } | null;
      }
    | null
    | undefined,
): AnswerInput => ({
  text: answer?.text ?? null,
  date1: answer?.date1 ?? null,
  date2: answer?.date2 ?? null,
  // Older answers may only carry the joined enumResponse row; fall back to
  // its dbId so radio/select controls preselect and re-saves keep the choice.
  answerId: answer?.answerId ?? answer?.enumResponse?.dbId ?? null,
  questionText: answer?.questionText ?? null,
});

interface ProgramQuestionPaneProps {
  input: AnswerInput;
  onInputChange: (next: AnswerInput) => void;
  localError: string | null;
  disabled?: boolean;
  report?: ReportsTable;
  onHideFooterChange?: (hide: boolean) => void;
  onBlockedChange?: (blocked: boolean) => void;
  onPrefilledCtaClick?: () => void;
  prefilledCtaLabel?: string;
  prefilledCtaDisabled?: boolean;
}

const ProgramQuestionPane: React.FC<ProgramQuestionPaneProps> = ({
  input,
  onInputChange,
  localError,
  disabled,
  report,
  onHideFooterChange,
  onBlockedChange,
  onPrefilledCtaClick,
  prefilledCtaLabel = 'Save & Continue',
  prefilledCtaDisabled,
}) => {
  const {
    activeQuestionId,
    findQuestionById,
    questionStatus,
    questions,
    programName,
    getProgramProfileAnswer,
    getHelperText,
  } = useProgram();

  const found = useMemo(() => findQuestionById(activeQuestionId), [
    activeQuestionId,
    findQuestionById,
  ]);

  const foundQuestionId = found?.question.questionId;
  const activityHelperTitle = useMemo(() => {
    if (foundQuestionId === undefined) {
      return undefined;
    }
    const questionId = Number(foundQuestionId);
    if (!HELPER_TEXT_AS_TITLE_QUESTION_IDS.includes(questionId)) {
      return undefined;
    }
    const answerTextFor = (id: number): string | undefined => {
      const a = findQuestionById(id)?.question.answer;
      const text = a?.text ?? a?.questionText;
      return text && text.trim().length > 0 ? text : undefined;
    };
    return getQuestionOptions(
      questionId,
      answerTextFor(serviceDeliveryQuestionId),
      answerTextFor(serviceSubTypeQuestionId),
    )?.helperText;
  }, [foundQuestionId, findQuestionById]);

  if (questions.type === 'REQUEST_START') {
    return (
      <Wrapper>
        <div className="empty">
          <TrueImpactText>Loading questions...</TrueImpactText>
        </div>
      </Wrapper>
    );
  }

  if (!found) {
    return (
      <Wrapper>
        <div className="empty">
          <TrueImpactText>
            Select a question from the sidebar to get started.
          </TrueImpactText>
        </div>
      </Wrapper>
    );
  }

  const { question } = found;
  const status = questionStatus(question);
  const isActivity = isActivityQuestion(question);
  const prefilled = isPrefilledFromProgram(question, getProgramProfileAnswer);
  // Impact-summary questions render contextual helper text inside their
  // field (via <HelperText />), which supersedes the static DB helpContent
  // just like ReportBuilder's getHelpText override does.
  const hasHelperOverride =
    getHelperText(question.section, question.questionId) != null ||
    question.questionId === CAPACITY_DEVELOPMENT_QUESTION_ID;

  const titleContent = activityHelperTitle
    ? activityHelperTitle
    : isActivity
    ? getActivityQuestionText(
        question.questionId,
        programName,
        undefined,
        undefined,
        question.text ?? undefined,
        question.name ?? question.text ?? `Question ${question.questionId}`,
      )
    : question.textNode ||
      question.text ||
      question.name ||
      `Question ${question.questionId}`;

  return (
    <Wrapper>
      <div className="question-card">
        <div className="question-header">
          <StatusIcon status={status} size={22} />
          <TrueImpactText
            className="question-title-text"
            variant="heading-subheading"
          >
            {titleContent}
            {question.required && <span className="required-badge">*</span>}
          </TrueImpactText>
        </div>
        {question.helpContent && !hasHelperOverride && (
          <div className="help-text">
            <TrueImpactText variant="body-small" color="greyDark">
              {question.helpContent}
            </TrueImpactText>
          </div>
        )}
        {prefilled && (
          <div className="prefilled-callout" role="status">
            <span className="prefilled-icon" aria-hidden>
              <FontAwesomeIcon icon={faSparkles} />
            </span>
            <div className="prefilled-copy">
              <TrueImpactText
                variant="body-small-bold"
                className="prefilled-title"
              >
                We filled this from your program profile
              </TrueImpactText>
              <TrueImpactText variant="body-small" className="prefilled-body">
                It isn&apos;t part of this report yet. Review or tweak it, then
                save to lock it in.
              </TrueImpactText>
            </div>
            <button
              type="button"
              className="prefilled-cta"
              onClick={onPrefilledCtaClick}
              disabled={prefilledCtaDisabled}
            >
              {prefilledCtaLabel}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}
        {isActivity ? (
          <ActivityQuestionRenderer
            question={question}
            value={input}
            onChange={onInputChange}
            disabled={disabled}
            onBlockedChange={onBlockedChange}
          />
        ) : (
          <QuestionRenderer
            key={question.questionId}
            question={question}
            value={input}
            onChange={onInputChange}
            disabled={disabled}
            report={report}
            onHideFooterChange={onHideFooterChange}
            onBlockedChange={onBlockedChange}
          />
        )}
      </div>
    </Wrapper>
  );
};

export default ProgramQuestionPane;
